import type { ModelMessage } from "ai";

export const AI_ATTACHMENT_INPUT_TYPES = ["image", "audio", "video", "file"] as const;
export type AiAttachmentInputType = (typeof AI_ATTACHMENT_INPUT_TYPES)[number];
export type AiInputLimits = Partial<Record<"total" | AiAttachmentInputType, number | null>>;
export type AiInputCounts = Record<AiAttachmentInputType, number>;
export type AiInputLimitIssue = { type: "total" | AiAttachmentInputType; count: number; max: number };
export const INPUT_COUNT_ERROR = "数量上限须为正整数，留空表示不额外限制";

export function normalizeInputLimits(value: unknown, enabled?: readonly string[]): AiInputLimits {
  if (value === undefined || value === null) return {};
  if (typeof value !== "object" || Array.isArray(value)) throw new Error(INPUT_COUNT_ERROR);
  const raw = value as Record<string, unknown>;
  const result: AiInputLimits = {};
  for (const key of ["total", ...AI_ATTACHMENT_INPUT_TYPES] as const) {
    const count = raw[key];
    if (count === undefined) continue;
    if (count !== null && (typeof count !== "number" || !Number.isSafeInteger(count) || count <= 0 || count > 2_147_483_647)) throw new Error(INPUT_COUNT_ERROR);
    if (key !== "total" && enabled && !enabled.includes(key)) continue;
    result[key] = count as number | null;
  }
  return result;
}

export function attachmentInputType(mediaType: string): AiAttachmentInputType {
  const type = mediaType.split("/")[0];
  return type === "image" || type === "audio" || type === "video" ? type : "file";
}

export function inputCounts(files: readonly { mediaType: string }[]): AiInputCounts {
  const counts: AiInputCounts = { image: 0, audio: 0, video: 0, file: 0 };
  for (const file of files) counts[attachmentInputType(file.mediaType)]++;
  return counts;
}

export function inputCountLimitIssue(limits: AiInputLimits | undefined, counts: AiInputCounts): AiInputLimitIssue | null {
  const normalized = normalizeInputLimits(limits);
  const total = Object.values(counts).reduce((sum, count) => sum + count, 0);
  for (const type of ["total", ...AI_ATTACHMENT_INPUT_TYPES] as const) {
    const max = normalized[type];
    const count = type === "total" ? total : counts[type];
    if (max != null && count > max) return { type, count, max };
  }
  return null;
}

export function attachmentLimitIssue(limits: AiInputLimits | undefined, files: readonly { mediaType: string }[]) {
  return inputCountLimitIssue(limits, inputCounts(files));
}

export function remainingAttachmentCount(limits: AiInputLimits | undefined, currentCount: number): number | undefined {
  return limits?.total == null ? undefined : Math.max(0, limits.total - currentCount);
}

/** Counts native media on every SDK step. Extracted documents retain their source-file count. */
export function modelMessageInputCounts(messages: readonly ModelMessage[], extractedDocuments = 0): AiInputCounts {
  const counts: AiInputCounts = { image: 0, audio: 0, video: 0, file: extractedDocuments };
  const visit = (parts: readonly unknown[]) => {
    for (const item of parts) {
      if (!item || typeof item !== "object") continue;
      const part = item as Record<string, unknown>;
      if (["image", "image-data", "image-url", "image-file-id", "image-file-reference"].includes(String(part.type))) counts.image++;
      else if (["file", "file-data", "file-url", "file-id", "file-reference"].includes(String(part.type))) counts[attachmentInputType(String(part.mediaType ?? ""))]++;
      else if (part.type === "tool-result" && part.output && typeof part.output === "object") {
        const output = part.output as Record<string, unknown>;
        if (output.type === "content" && Array.isArray(output.value)) visit(output.value);
      }
    }
  };
  for (const message of messages) if (Array.isArray(message.content)) visit(message.content);
  return counts;
}
